import React, { useCallback, useId } from 'react';
import { PapiDeviation } from '@wix/da-papi-types';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { BiContextProvidedValue, BiData, MoreMenuClickBiEvent } from '@wix/da-bi/pkg/events';
import { getDeviationResourceType } from '../../_helpers';
import PopperDropdownManager, {
  type Props as PopperDropdownManagerProps,
} from '@wix/da-ds/pkg/Dropdown/PopperDropdownLayout/PopperDropdownManager';
import PopperDropdownToggle from '@wix/da-ds/pkg/Dropdown/PopperDropdownLayout/PopperDropdownToggle';
import StandardDropdownMenu from '@wix/da-ds/pkg/Dropdown/Preset/StandardDropdown/StandardDropdownMenu';
import { useDropdownItems } from '@wix/da-ds/pkg/Dropdown/hooks/useDropdownItems';
import { NullableDropdownItem } from '@wix/da-ds/pkg/Dropdown/types';
import s from './ThumbActions.scss';

export interface Props extends Pick<PopperDropdownManagerProps, 'onOpen'> {
  deviation?: PapiDeviation;
  actions: NullableDropdownItem[];
  className?: string;
  children?: React.ReactNode;
}

const ThumbActions: React.FC<React.PropsWithChildren<Props>> = ({
  deviation,
  actions,
  className,
  children,
  onOpen,
}) => {
  const dropdownId = useId();
  const logger = useBiLogger();
  const { filteredItems } = useDropdownItems(actions);
  const typeid = getDeviationResourceType(deviation);

  const handleOpen = useCallback(() => {
    if (!deviation?.deviationId) {
      return;
    }

    logger(
      BiData<MoreMenuClickBiEvent>({
        evid: 417,
        typeid,
        itemid: deviation.deviationId,
        sectionname: BiContextProvidedValue,
        sort_type: BiContextProvidedValue,
        reason_shown: BiContextProvidedValue,
        rank: BiContextProvidedValue,
        feed_mode: BiContextProvidedValue,
        sectionid: BiContextProvidedValue,
        section_typeid: BiContextProvidedValue,
        is_grouped: BiContextProvidedValue,
      })
    );
    onOpen && onOpen();
  }, [logger, typeid, deviation?.deviationId, onOpen]);

  if (!filteredItems.length) {
    return null;
  }

  return (
    <PopperDropdownManager onOpen={handleOpen}>
      <PopperDropdownToggle className={className} aria-owns={dropdownId}>
        {children}
      </PopperDropdownToggle>
      <StandardDropdownMenu
        id={dropdownId}
        items={filteredItems}
        role="menu"
        className={s['menu']}
      />
    </PopperDropdownManager>
  );
};
ThumbActions.displayName = 'ThumbActions';

export default ThumbActions;
